<template>
  <div :class="{ 'nav-open': $sidebar.showSidebar }">
    <notifications></notifications>
    <router-view></router-view>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import { getDomainContext, getWebsiteOrigin, isCliprHostname, leaveAppForWebsite } from "@/utils/auth";
import { initializeLocale } from "@/i18n";
import { IMPERSONATION_EVENT } from "@/utils/impersonation";
import { useCurrentUserStore } from "@/stores/currentUser";
import { useSocialProfileStore } from "@/stores/socialProfile";
import { useFacebookPageIdStore } from "@/stores/facebookPageId";
import { buildMetaAdvancedMatching } from "@/api/session";
import {
  initMetaPixel,
  trackMetaStandardEvent,
  trackPageView,
} from "@/utils/metaPixel";

const CLIPR_AUTH_HANDOFF_COOKIE_NAME = "clipr_auth_handoff";
const SIGNUP_ATTRIBUTION_STORAGE_KEY = "clipr_signup_attribution";

export default {
  name: "App",
  methods: {
    enforceCanonicalCliprHost() {
      const { hostname, pathname, search, hash } = window.location;
      const normalizedHost = String(hostname || "").toLowerCase();
      if (normalizedHost === "localhost" || normalizedHost === "127.0.0.1" || normalizedHost === "::1") {
        return false;
      }
      if (
        normalizedHost.endsWith(".ngrok-free.app") ||
        normalizedHost.endsWith(".ngrok.app") ||
        normalizedHost.endsWith(".ngrok.io")
      ) {
        return false;
      }
      if (!isCliprHostname(hostname) || hostname === "app.clipr.tv") return false;
      const canonicalUrl = `https://app.clipr.tv${pathname}${search}${hash}`;
      window.location.replace(canonicalUrl);
      return true;
    },
    parseOAuthOriginData() {
      const raw = localStorage.getItem("oauth_origin");
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (e) {
        localStorage.removeItem("oauth_origin");
        return null;
      }
    },
    readCookieValue(name) {
      const cookies = String(document.cookie || "").split(";");
      const prefix = `${name}=`;
      for (const rawCookie of cookies) {
        const cookie = rawCookie.trim();
        if (!cookie.startsWith(prefix)) continue;
        return decodeURIComponent(cookie.slice(prefix.length));
      }
      return "";
    },
    clearCookie(name, domain = "") {
      const domainPart = domain ? `; domain=${domain}` : "";
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax; Secure${domainPart}`;
    },
    clearAuthHandoffCookie() {
      this.clearCookie(CLIPR_AUTH_HANDOFF_COOKIE_NAME, ".clipr.tv");
      this.clearCookie(CLIPR_AUTH_HANDOFF_COOKIE_NAME);
    },
    normalizeSignupAttribution(rawAttribution) {
      if (!rawAttribution || typeof rawAttribution !== "object") return null;
      const sourceUrl =
        typeof rawAttribution.meta_event_source_url === "string" &&
        rawAttribution.meta_event_source_url.trim()
          ? rawAttribution.meta_event_source_url.trim()
          : window.location.href;
      return {
        meta_event_source_url: sourceUrl,
        meta_fbp:
          typeof rawAttribution.meta_fbp === "string"
            ? rawAttribution.meta_fbp
            : "",
        meta_fbc:
          typeof rawAttribution.meta_fbc === "string"
            ? rawAttribution.meta_fbc
            : "",
      };
    },
    async consumeWebsiteAuthHandoff() {
      if (typeof document === "undefined" || typeof localStorage === "undefined") return null;
      const rawCookie = this.readCookieValue(CLIPR_AUTH_HANDOFF_COOKIE_NAME);
      if (!rawCookie) return null;
      this.clearAuthHandoffCookie();

      let parsed = null;
      try {
        parsed = JSON.parse(rawCookie);
      } catch (error) {
        return null;
      }
      if (!parsed || typeof parsed !== "object" || !this.isFreshOAuthData(parsed.timestamp)) {
        return null;
      }

      const safeRedirectPath = this.getSafeRedirectPath(parsed.redirectPath || "/create-clips");
      const normalizedOrigin = String(parsed.origin || "https://clipr.tv").replace(/\/+$/, "");
      let contextHostname = "";
      try {
        contextHostname = new URL(normalizedOrigin).hostname;
      } catch (error) {
        contextHostname = "";
      }
      const flow = parsed.flow === "gis" ? "gis" : "oauth";
      localStorage.setItem(
        "oauth_origin",
        JSON.stringify({
          origin: normalizedOrigin,
          context: parsed.context || getDomainContext(contextHostname),
          flow,
          redirectPath: safeRedirectPath,
          timestamp: Date.now(),
        })
      );

      const attribution = this.normalizeSignupAttribution(parsed.attribution);
      if (attribution) {
        localStorage.setItem(
          SIGNUP_ATTRIBUTION_STORAGE_KEY,
          JSON.stringify(attribution)
        );
      }

      if (
        parsed.session?.access_token &&
        parsed.session?.refresh_token
      ) {
        const { error } = await supabase.auth.setSession({
          access_token: parsed.session.access_token,
          refresh_token: parsed.session.refresh_token,
        });
        if (error) {
          console.error("Failed to restore GIS session from website handoff:", error);
          return parsed;
        }

        localStorage.removeItem("oauth_origin");

        if (this.$route.path === "/login" || this.$route.path === "/") {
          this.$router.push(safeRedirectPath);
        }
      }

      return parsed;
    },
    isCliprWebsiteOrigin(origin) {
      if (typeof origin !== "string" || origin.trim() === "") return false;
      try {
        const normalizedOrigin = new URL(origin).origin.toLowerCase();
        const localWebsiteOrigin = String(
          getWebsiteOrigin({ forceExternalOrigin: true }) || "https://clipr.tv"
        )
          .replace(/\/+$/, "")
          .toLowerCase();
        return (
          normalizedOrigin === "https://clipr.tv" ||
          normalizedOrigin === "https://www.clipr.tv" ||
          normalizedOrigin === localWebsiteOrigin
        );
      } catch (error) {
        return false;
      }
    },
    getWebsiteOAuthRedirectPath() {
      const storedOriginData = this.parseOAuthOriginData();
      if (!storedOriginData) return null;
      if (storedOriginData.flow !== "oauth") return null;
      if (!this.isFreshOAuthData(storedOriginData.timestamp)) return null;
      if (!this.isCliprWebsiteOrigin(storedOriginData.origin)) return null;
      return "/create-clips";
    },
    getSafeRedirectPath() {
      return "/create-clips";
    },
    hasOAuthHashFragment() {
      const rawHash = String(window.location.hash || "");
      if (!rawHash) return false;
      return (
        rawHash.includes("access_token=") ||
        rawHash.includes("refresh_token=") ||
        rawHash.includes("error=")
      );
    },
    clearOAuthHashFragmentFromUrl() {
      if (typeof window === "undefined") return;
      if (!this.hasOAuthHashFragment()) return;
      const cleanUrl = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState(null, "", cleanUrl);
    },
    getPreferredAuthenticatedRoute() {
      const websiteOAuthRedirectPath = this.getWebsiteOAuthRedirectPath();
      if (websiteOAuthRedirectPath) {
        localStorage.removeItem("oauth_origin");
        return websiteOAuthRedirectPath;
      }
      return "/create-clips";
    },
    isFreshOAuthData(timestamp) {
      if (!timestamp) return false;
      const fiveMinutes = 5 * 60 * 1000;
      return Date.now() - timestamp < fiveMinutes;
    },
    shouldRedirectCrossOrigin(storedData, currentOrigin) {
      if (!storedData?.origin || !this.isFreshOAuthData(storedData.timestamp)) return false;
      if (storedData.origin === currentOrigin) return false;
      if (storedData.flow !== "oauth") return false;

      const currentIsClipr = isCliprHostname(window.location.hostname);
      let storedHostname = "";
      try {
        storedHostname = new URL(storedData.origin).hostname;
      } catch (e) {
        storedHostname = "";
      }
      const storedContext = storedData.context || getDomainContext(storedHostname);

      // Prevent involuntary redirects from Twitch (Clipr) back to Momo.
      if (storedContext === "clipr" && currentIsClipr) return false;
      return true;
    },
    async bootstrapCurrentUser(session, { force = false, syncBilling = false } = {}) {
      const currentUserStore = useCurrentUserStore();
      const userInfo = await currentUserStore.ensureSessionReady(session, {
        force,
        syncCommerce: syncBilling,
      });
      initializeLocale(currentUserStore.savedLocale);
      return userInfo;
    },
    initializeMetaTracking(session) {
      const currentUserStore = useCurrentUserStore();
      const profile = currentUserStore.profile;
      const metadata = session?.user?.user_metadata || {};
      const fullName = profile?.use_name || metadata.full_name || metadata.name || "";
      const email = profile?.user_email || session?.user?.email || "";
      const userId = profile?.user_id || session?.user?.id || "";

      initMetaPixel(buildMetaAdvancedMatching({
        email,
        fullName,
        externalId: userId,
        phone: profile?.user_phone || "",
      }));

      if (!currentUserStore.ensuredProfile?.created || !userId) return;

      trackMetaStandardEvent(
        "CompleteRegistration",
        null,
        { eventID: `reg-${userId}` }
      );
    },
    async handleImpersonationChange(event) {
      if (event?.type === "storage" && event.key && event.key !== "momo_impersonation") return;
      try {
        const currentUserStore = useCurrentUserStore();
        const socialProfileStore = useSocialProfileStore();
        const facebookPageIdStore = useFacebookPageIdStore();
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error || !session) {
          currentUserStore.clear();
          socialProfileStore.clear();
          facebookPageIdStore.clear();
          initializeLocale();
          return;
        }
        socialProfileStore.clear();
        facebookPageIdStore.clear();
        await currentUserStore.refresh(session, { syncCommerce: true });
        initializeLocale(currentUserStore.savedLocale);
      } catch (error) {
        console.error("Error refreshing current user after impersonation change:", error);
      }
    },
  },
  created() {
    const currentUserStore = useCurrentUserStore();
    const socialProfileStore = useSocialProfileStore();
    const facebookPageIdStore = useFacebookPageIdStore();
    // Listen for auth state changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        const websiteLocale = this.$i18n.locale;
        currentUserStore.clear();
        socialProfileStore.clear();
        facebookPageIdStore.clear();
        initializeLocale();
        // User signed out or session expired
        const requiresAuth = this.$route?.matched?.some((record) => record.meta?.requiresAuth);
        if (requiresAuth) {
          leaveAppForWebsite({ locale: websiteLocale });
        }
      } else if (event === "SIGNED_IN" && session) {
        try {
          await this.bootstrapCurrentUser(session, { syncBilling: true });
          this.initializeMetaTracking(session);
        } catch (error) {
          console.error("Error bootstrapping current user after sign in:", error);
          return;
        }
        this.clearOAuthHashFragmentFromUrl();

        const storedOriginData = this.parseOAuthOriginData();
        const currentOrigin = window.location.origin;

        if (storedOriginData) {
          if (!this.isFreshOAuthData(storedOriginData.timestamp) || storedOriginData.origin === currentOrigin) {
            localStorage.removeItem("oauth_origin");
          }
        }

        if (this.$route.path === "/login" || this.$route.path === "/") {
          this.$router.push(this.getPreferredAuthenticatedRoute());
        }
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("storage", this.handleImpersonationChange);
    window.removeEventListener(IMPERSONATION_EVENT, this.handleImpersonationChange);
  },
  async mounted() {
    // Use app.clipr.tv as canonical host for Twitch flow.
    if (this.enforceCanonicalCliprHost()) return;

    initMetaPixel();
    trackPageView();
    await this.consumeWebsiteAuthHandoff();

    const currentUserStore = useCurrentUserStore();
    initializeLocale(currentUserStore.savedLocale);
    window.addEventListener("storage", this.handleImpersonationChange);
    window.addEventListener(IMPERSONATION_EVENT, this.handleImpersonationChange);

    // Handle OAuth callback redirect fix
    // If we're on production but started OAuth from localhost, redirect back
    const storedOriginData = this.parseOAuthOriginData();
    const currentOrigin = window.location.origin;

    // Check if we have OAuth callback indicators (hash fragments or query params)
    const hasOAuthCallback = window.location.hash.includes('access_token') || 
                            window.location.hash.includes('error') ||
                            window.location.search.includes('code=');

    if (storedOriginData) {
      try {
        const shouldCrossRedirect = this.shouldRedirectCrossOrigin(storedOriginData, currentOrigin);
        // If we're on a different origin than where we started OAuth
        if (hasOAuthCallback && shouldCrossRedirect) {
          // Wait a bit for Supabase to process the session
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Check for session (OAuth might have completed)
          const { data: { session } } = await supabase.auth.getSession();
          
          // Redirect if we have a session OR if we detect OAuth callback indicators
          if (session || hasOAuthCallback) {
            localStorage.removeItem("oauth_origin"); // Clear after use
            const safePath = this.getSafeRedirectPath(storedOriginData.redirectPath);
            
            // Include hash/query params in redirect to preserve OAuth callback data
            const redirectUrl = hasOAuthCallback 
              ? `${storedOriginData.origin}${window.location.pathname}${window.location.search}${window.location.hash}`
              : `${storedOriginData.origin}${safePath}`;
            
            window.location.href = redirectUrl;
            return;
          }
        } else if (
          storedOriginData.origin === currentOrigin ||
          !this.isFreshOAuthData(storedOriginData.timestamp)
        ) {
          // Clear stale or same-origin data
          localStorage.removeItem("oauth_origin");
        }
      } catch (e) {
        console.error("Error handling OAuth redirect:", e);
        // Invalid stored data, clear it
        localStorage.removeItem("oauth_origin");
      }
    }
    
    // Handle OAuth callback if user is returning from OAuth provider
    const { data: { session } } = await supabase.auth.getSession();
    this.clearOAuthHashFragmentFromUrl();
    if (!session) {
      currentUserStore.clear();
      useSocialProfileStore().clear();
      useFacebookPageIdStore().clear();
      initializeLocale();
    }
    if (session) {
      await this.bootstrapCurrentUser(session, { syncBilling: true });
      this.initializeMetaTracking(session);
    }
    if (session && this.$route.path === "/login") {
      // User is authenticated and on login page, open Create Clips.
      this.$router.push(this.getPreferredAuthenticatedRoute());
    }
  },
};
</script>

<style lang="scss">
@import "@/assets/sass/clipr/clipr-lighter";

.vue-notifyjs.notifications {
  .alert {
    z-index: 10000;
    position: fixed !important;
    width: min(420px, calc(100vw - 24px));
    padding: 14px 16px 14px 18px;
    margin: 0;
    border: 1px solid var(--clipr-border-color);
    border-radius: 14px;
    background: linear-gradient(
      140deg,
      rgba(15, 15, 24, 0.96) 0%,
      rgba(23, 23, 34, 0.93) 100%
    );
    color: var(--clipr-text-primary);
    box-shadow:
      0 18px 38px rgba(0, 0, 0, 0.45),
      0 0 28px rgba(168, 85, 247, 0.12);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    transition:
      transform 0.22s ease,
      opacity 0.22s ease,
      box-shadow 0.22s ease,
      border-color 0.22s ease;
    cursor: pointer;
    overflow: hidden;
    line-height: 1.45;
  }

  .alert:hover {
    transform: translateY(-2px);
    border-color: var(--clipr-border-hover);
    box-shadow:
      0 22px 42px rgba(0, 0, 0, 0.48),
      0 0 34px rgba(168, 85, 247, 0.2);
  }

  .alert[data-notify="container"] {
    border-left: 4px solid var(--clipr-purple-light);
  }

  .alert.alert-success[data-notify="container"] {
    border-left-color: #22c55e;
    background: linear-gradient(
      140deg,
      rgba(10, 26, 19, 0.96) 0%,
      rgba(16, 35, 27, 0.93) 100%
    );
    color: #c4f5db;
  }

  .alert.alert-danger[data-notify="container"] {
    border-left-color: #ef4444;
    background: linear-gradient(
      140deg,
      rgba(31, 13, 18, 0.96) 0%,
      rgba(42, 15, 22, 0.93) 100%
    );
    color: #ffd1d1;
  }

  .alert.alert-warning[data-notify="container"] {
    border-left-color: #f59e0b;
    background: linear-gradient(
      140deg,
      rgba(32, 21, 8, 0.96) 0%,
      rgba(42, 29, 12, 0.93) 100%
    );
    color: #ffe7b8;
  }

  .alert.alert-info[data-notify="container"] {
    border-left-color: #38bdf8;
    background: linear-gradient(
      140deg,
      rgba(10, 21, 34, 0.96) 0%,
      rgba(13, 30, 48, 0.93) 100%
    );
    color: #cfeeff;
  }

  .alert .close {
    display: none !important;
  }

  .alert .close:hover {
    color: inherit;
    opacity: inherit;
  }

  .alert .alert-icon,
  .alert span[data-notify="icon"] {
    display: none !important;
  }

  .alert.alert-with-icon {
    padding-left: 18px;
  }

  .alert [data-notify="message"] {
    display: inline-block;
    font-size: 13.5px;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: inherit;
  }

  .alert .close ~ span {
    max-width: 92%;
  }

  .alert.left {
    left: 12px;
  }

  .alert.right {
    right: 12px;
  }

  .alert.center {
    left: 50%;
    transform: translateX(-50%);
  }

  .list-move {
    transition: transform 0.3s, opacity 0.4s;
  }
  .list-item {
    display: inline-block;
    margin-right: 0;
  }
  .list-enter-active {
    transition: transform 0.22s ease, opacity 0.22s ease;
  }
  .list-leave-active {
    transition: transform 0.22s ease, opacity 0.22s ease;
  }

  .list-enter {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }
  .list-leave-to {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }
}

@media (max-width: 768px) {
  .vue-notifyjs.notifications {
    .alert {
      width: calc(100vw - 18px);
      padding: 12px 14px 12px 15px;
      border-radius: 12px;
    }

    .alert.left,
    .alert.right {
      left: 9px;
      right: 9px;
    }

    .alert.center {
      left: 9px;
      right: 9px;
      transform: none;
    }
  }
}

</style>
